import { create } from "zustand"
import { persist } from "zustand/middleware"
import { useTagTaxonomyStore, type TagCategory } from "./tag-taxonomy-store"

export interface SuggestedTag {
  id: string
  tag: string
  contentId: string // "site:postId"
  suggestedBy: string
  upvotes: number
  downvotes: number
  isApproved: boolean
  isRejected: boolean
  createdAt: number
  approvedAt?: number
  rejectedAt?: number
}

export interface TagDispute {
  id: string
  tag: string
  contentId: string
  reason: string
  suggestedBy: string
  upvotes: number
  downvotes: number
  isApproved: boolean
  createdAt: number
  approvedAt?: number
}

export interface LockedTag {
  tag: string
  reason: "disputed-readded" | "approved-disputed-readded" // Why it's locked
  lockedAt: number
  lockedBy: string
}

interface SuggestedTagsState {
  suggestedTags: SuggestedTag[]
  disputes: TagDispute[]
  lockedTags: LockedTag[]

  // Actions
  suggestTag: (tag: string, contentId: string, userId: string, initialUpvotes?: number) => void
  upvoteTag: (tagId: string, userId: string) => void
  downvoteTag: (tagId: string, userId: string) => void
  removeUpvote: (tagId: string) => void
  removeDownvote: (tagId: string) => void
  getTagsForContent: (contentId: string) => SuggestedTag[]
  getSuggestedTagsByUser: (userId: string) => SuggestedTag[]
  getApprovedTags: (contentId: string) => SuggestedTag[]
  getPendingTags: () => SuggestedTag[]
  approveTag: (tagId: string) => void
  rejectTag: (tagId: string) => void
  autoApproveAllPending: () => number
  createDispute: (tag: string, contentId: string, reason: string, userId: string) => void
  upvoteDispute: (disputeId: string, userId: string) => void
  downvoteDispute: (disputeId: string, userId: string) => void
  removeDisputeUpvote: (disputeId: string) => void
  removeDisputeDownvote: (disputeId: string) => void
  getDisputesForContent: (contentId: string) => TagDispute[]
  getDisputedTagsForContent: (contentId: string) => string[]
  isTagLocked: (tag: string) => boolean
  lockTag: (tag: string, reason: "disputed-readded" | "approved-disputed-readded", userId: string) => void
  getLockedTagReason: (tag: string) => string | null
}

/**
 * Helper function to determine tag category from contentId and tag format
 */
function inferTagCategoryFromSuggestion(contentId: string, tag: string): TagCategory {
  // Check if it's from the artist panel
  if (contentId.startsWith("artist:")) {
    // Check tag format to determine specific artist type
    if (tag.includes("voiced_by_") || tag.includes("_(va)")) {
      return "va"
    }
    if (tag.includes("sound_design_by_") || tag.includes("_(sound_designer)")) {
      return "sound_designer"
    }
    return "artist"
  }

  // Check tag format for other categories
  const lowerTag = tag.toLowerCase()
  
  if (lowerTag.includes("_(character)") || lowerTag.includes("character:")) {
    return "character"
  }
  
  if (lowerTag.includes("_(series)") || lowerTag.includes("copyright:") || lowerTag.includes("_(franchise)")) {
    return "series"
  }
  
  // Default to general for regular tag suggestions
  return "general"
}

export const useSuggestedTagsStore = create<SuggestedTagsState>()(
  persist(
    (set, get) => ({
      suggestedTags: [],
      disputes: [],
      lockedTags: [],

      suggestTag: (tag, contentId, userId, initialUpvotes = 0) => {
        const id = `tag-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        const newTag: SuggestedTag = {
          id,
          tag,
          contentId,
          suggestedBy: userId,
          upvotes: initialUpvotes,
          downvotes: 0,
          isApproved: false,
          isRejected: false,
          createdAt: Date.now(),
        }
        set((state) => ({ suggestedTags: [...state.suggestedTags, newTag] }))
      },

      upvoteTag: (tagId, userId) => {
        set((state) => {
          const updated = state.suggestedTags.map((tag) => {
            if (tag.id === tagId) {
              const newUpvotes = tag.upvotes + 1
              const isApproved = newUpvotes >= 6 && !tag.isApproved && !tag.isRejected
              
              // Auto-categorize when approved
              if (isApproved) {
                const category = inferTagCategoryFromSuggestion(tag.contentId, tag.tag)
                const { categorizeTag } = useTagTaxonomyStore.getState()
                categorizeTag(tag.tag, category, userId)
              }
              
              return {
                ...tag,
                upvotes: newUpvotes,
                isApproved: tag.isApproved || isApproved,
                approvedAt: isApproved ? Date.now() : tag.approvedAt,
              }
            }
            return tag
          })
          return { suggestedTags: updated }
        })
      },

      downvoteTag: (tagId, userId) => {
        set((state) => {
          const updated = state.suggestedTags.map((tag) => {
            if (tag.id === tagId) {
              const newDownvotes = tag.downvotes + 1
              const isRejected = newDownvotes >= 6 && !tag.isApproved && !tag.isRejected
              return {
                ...tag,
                downvotes: newDownvotes,
                isRejected: tag.isRejected || isRejected,
                rejectedAt: isRejected ? Date.now() : tag.rejectedAt,
              }
            }
            return tag
          })
          return { suggestedTags: updated }
        })
      },

      removeUpvote: (tagId) => {
        set((state) => {
          const updated = state.suggestedTags.map((tag) => {
            if (tag.id === tagId) {
              const newUpvotes = Math.max(0, tag.upvotes - 1)
              const shouldDemote = newUpvotes < 6 && tag.isApproved
              return {
                ...tag,
                upvotes: newUpvotes,
                isApproved: tag.isApproved && !shouldDemote,
              }
            }
            return tag
          })
          return { suggestedTags: updated }
        })
      },

      removeDownvote: (tagId) => {
        set((state) => {
          const updated = state.suggestedTags.map((tag) => {
            if (tag.id === tagId) {
              const newDownvotes = Math.max(0, tag.downvotes - 1)
              const shouldDemote = newDownvotes < 6 && tag.isRejected
              return {
                ...tag,
                downvotes: newDownvotes,
                isRejected: tag.isRejected && !shouldDemote,
              }
            }
            return tag
          })
          return { suggestedTags: updated }
        })
      },

      getTagsForContent: (contentId) => {
        return get()
          .suggestedTags.filter((t) => t.contentId === contentId && !t.isRejected)
          .sort((a, b) => b.upvotes - a.upvotes)
      },

      getSuggestedTagsByUser: (userId) => {
        return get().suggestedTags.filter((t) => t.suggestedBy === userId)
      },

      getApprovedTags: (contentId) => {
        return get()
          .suggestedTags.filter((t) => t.contentId === contentId && t.isApproved && !t.isRejected)
          .sort((a, b) => b.upvotes - a.upvotes)
      },

      getPendingTags: () => {
        return get().suggestedTags.filter((t) => !t.isApproved && !t.isRejected)
      },

      approveTag: (tagId) => {
        set((state) => {
          const updated = state.suggestedTags.map((tag) => {
            if (tag.id === tagId && !tag.isApproved && !tag.isRejected) {
              // Auto-categorize when manually approved
              const category = inferTagCategoryFromSuggestion(tag.contentId, tag.tag)
              const { categorizeTag } = useTagTaxonomyStore.getState()
              categorizeTag(tag.tag, category)
              
              return {
                ...tag,
                isApproved: true,
                approvedAt: Date.now(),
              }
            }
            return tag
          })
          return { suggestedTags: updated }
        })
      },

      rejectTag: (tagId) => {
        set((state) => {
          const updated = state.suggestedTags.map((tag) => {
            if (tag.id === tagId && !tag.isApproved && !tag.isRejected) {
              return {
                ...tag,
                isRejected: true,
                rejectedAt: Date.now(),
              }
            }
            return tag
          })
          return { suggestedTags: updated }
        })
      },

      autoApproveAllPending: () => {
        let approvedCount = 0
        set((state) => {
          const updated = state.suggestedTags.map((tag) => {
            if (!tag.isApproved && !tag.isRejected) {
              approvedCount++
              
              // Auto-categorize when bulk approved
              const category = inferTagCategoryFromSuggestion(tag.contentId, tag.tag)
              const { categorizeTag } = useTagTaxonomyStore.getState()
              categorizeTag(tag.tag, category)
              
              return {
                ...tag,
                isApproved: true,
                approvedAt: Date.now(),
              }
            }
            return tag
          })
          return { suggestedTags: updated }
        })
        return approvedCount
      },

      createDispute: (tag, contentId, reason, userId) => {
        const id = `dispute-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        const newDispute: TagDispute = {
          id,
          tag,
          contentId,
          reason,
          suggestedBy: userId,
          upvotes: 1,
          downvotes: 0,
          isApproved: false,
          createdAt: Date.now(),
        }
        set((state) => ({ disputes: [...state.disputes, newDispute] }))
      },

      upvoteDispute: (disputeId, userId) => {
        set((state) => {
          const updated = state.disputes.map((dispute) => {
            if (dispute.id === disputeId) {
              const newUpvotes = dispute.upvotes + 1
              const isApproved = newUpvotes >= 6 && !dispute.isApproved
              return {
                ...dispute,
                upvotes: newUpvotes,
                isApproved: dispute.isApproved || isApproved,
                approvedAt: isApproved ? Date.now() : dispute.approvedAt,
              }
            }
            return dispute
          })
          return { disputes: updated }
        })
      },

      downvoteDispute: (disputeId, userId) => {
        set((state) => {
          const updated = state.disputes.map((dispute) => {
            if (dispute.id === disputeId) {
              return {
                ...dispute,
                downvotes: dispute.downvotes + 1,
              }
            }
            return dispute
          })
          return { disputes: updated }
        })
      },

      removeDisputeUpvote: (disputeId) => {
        set((state) => {
          const updated = state.disputes.map((dispute) => {
            if (dispute.id === disputeId) {
              const newUpvotes = Math.max(0, dispute.upvotes - 1)
              const shouldDemote = newUpvotes < 6 && dispute.isApproved
              return {
                ...dispute,
                upvotes: newUpvotes,
                isApproved: dispute.isApproved && !shouldDemote,
              }
            }
            return dispute
          })
          return { disputes: updated }
        })
      },

      removeDisputeDownvote: (disputeId) => {
        set((state) => {
          const updated = state.disputes.map((dispute) =>
            dispute.id === disputeId ? { ...dispute, downvotes: Math.max(0, dispute.downvotes - 1) } : dispute,
          )
          return { disputes: updated }
        })
      },

      getDisputesForContent: (contentId) => {
        return get()
          .disputes.filter((d) => d.contentId === contentId)
          .sort((a, b) => b.upvotes - a.upvotes)
      },

      getDisputedTagsForContent: (contentId) => {
        return get()
          .disputes.filter((d) => d.contentId === contentId && d.isApproved)
          .map((d) => d.tag)
      },

      isTagLocked: (tag) => {
        return get().lockedTags.some((lt) => lt.tag === tag)
      },

      lockTag: (tag, reason, userId) => {
        const existing = get().lockedTags.find((lt) => lt.tag === tag)
        if (!existing) {
          set((state) => ({
            lockedTags: [
              ...state.lockedTags,
              {
                tag,
                reason,
                lockedAt: Date.now(),
                lockedBy: userId,
              },
            ],
          }))
        }
      },

      getLockedTagReason: (tag) => {
        const locked = get().lockedTags.find((lt) => lt.tag === tag)
        if (!locked) return null
        return locked.reason === "disputed-readded"
          ? "This tag was disputed and re-added. It is now locked."
          : "This tag is officially verified. It is now locked."
      },
    }),
    {
      name: "suggested-tags-storage",
    },
  ),
)
